import React, { useState, useEffect } from "react";
import {
  createTeamMember,
  updateTeamMember,
  TeamMember,
  TeamMemberInput,
} from "../services/teamMembers";
import { getImageUrl } from "../services/api";
import "./TeamMemberForm.css";

interface TeamMemberFormProps {
  teamMember?: TeamMember | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const TeamMemberForm: React.FC<TeamMemberFormProps> = ({
  teamMember,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState<TeamMemberInput>({
    name: "",
    position: "",
    displayOrder: 0,
    isActive: true,
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (teamMember) {
      setFormData({
        name: teamMember.name,
        position: teamMember.position,
        displayOrder: teamMember.displayOrder,
        isActive: teamMember.isActive,
      });
      setPhotoPreview(getImageUrl(teamMember.photo));
    } else {
      // Reset form for new member
      setFormData({
        name: "",
        position: "",
        displayOrder: 0,
        isActive: true,
      });
      setPhotoPreview("");
      setPhotoFile(null);
    }
  }, [teamMember]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      isActive: e.target.checked,
    }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB");
        return;
      }

      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validation
      if (!formData.name.trim()) {
        throw new Error("Name is required");
      }
      if (!formData.position.trim()) {
        throw new Error("Position is required");
      }
      if (!teamMember && !photoFile) {
        throw new Error("Photo is required");
      }

      const submitData: TeamMemberInput = {
        ...formData,
        ...(photoFile && { photo: photoFile }),
      };

      if (teamMember) {
        // Update existing
        await updateTeamMember(teamMember.id, submitData);
      } else {
        // Create new
        await createTeamMember(submitData);
      }

      onSuccess();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to save team member"
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="team-member-form">
      <h2>{teamMember ? "Edit Team Member" : "Add New Team Member"}</h2>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter full name"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="position">
            Position <span className="required">*</span>
          </label>
          <input
            type="text"
            id="position"
            name="position"
            value={formData.position}
            onChange={handleInputChange}
            placeholder="e.g., CEO, Director, Manager"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="photo">
            Photo {!teamMember && <span className="required">*</span>}
          </label>
          <input
            type="file"
            id="photo"
            name="photo"
            accept="image/*"
            onChange={handlePhotoChange}
            disabled={loading}
          />
          <small className="form-hint">
            Max size: 5MB. Formats: JPG, PNG, GIF, WebP
          </small>

          {photoPreview && (
            <div className="photo-preview">
              <img src={photoPreview} alt="Preview" />
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="displayOrder">Display Order</label>
          <input
            type="number"
            id="displayOrder"
            name="displayOrder"
            value={formData.displayOrder}
            onChange={handleInputChange}
            min="0"
            disabled={loading}
          />
          <small className="form-hint">
            Lower numbers appear first (0 = highest priority)
          </small>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleCheckboxChange}
              disabled={loading}
            />
            <span>Active (show on website)</span>
          </label>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Saving..." : teamMember ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamMemberForm;
